import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import lpService from '../../services/api/lpService';
import '../../styles/lp-variables.css';

interface StepConfig {
  label: string;
  delay: number;
}

const steps: StepConfig[] = [
  { label: 'サービス情報を取得中', delay: 1500 },
  { label: '競合サービスを調査中', delay: 5500 },
  { label: 'ターゲットセグメントを分析中', delay: 9000 },
  { label: '訴求戦略を策定中', delay: 12000 },
];

type FieldName = 'name' | 'email' | 'company_name';

const fields: Array<{ name: FieldName; label: string; placeholder: string; type: string }> = [
  { name: 'name', label: 'お名前', placeholder: '田中 太郎', type: 'text' },
  { name: 'email', label: 'メールアドレス', placeholder: 'taro@example.com', type: 'email' },
  { name: 'company_name', label: '企業名', placeholder: '株式会社サンプル', type: 'text' },
];

/**
 * AnalyzingPage - Analysis animation + lead capture form
 * Step 2 of the onboarding flow:
 *   1. Shows analysis progress animation (13s)
 *   2. After completion, shows lead capture form
 *   3. On form submit, calls POST /api/leads and navigates to /complete
 */
const AnalyzingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const serviceUrl = searchParams.get('url') || '';

  const [visibleSteps, setVisibleSteps] = useState<number>(0);
  const [analysisComplete, setAnalysisComplete] = useState(false);

  // Lead capture form state
  const [formData, setFormData] = useState<Record<FieldName, string>>({
    name: '',
    email: '',
    company_name: '',
  });
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Redirect if no URL provided
  useEffect(() => {
    if (!serviceUrl) {
      navigate('/diagnose', { replace: true });
    }
  }, [serviceUrl, navigate]);

  // Analysis animation timers
  useEffect(() => {
    const timers = steps.map((step, i) =>
      setTimeout(() => setVisibleSteps((prev) => Math.max(prev, i + 1)), step.delay)
    );
    const completeTimer = setTimeout(() => setAnalysisComplete(true), 13000);
    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(completeTimer);
    };
  }, []);

  const handleChange = useCallback((name: FieldName, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  }, []);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<FieldName, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'お名前を入力してください';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'メールアドレスを入力してください';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'メールアドレスの形式が正しくありません';
    }

    if (!formData.company_name.trim()) {
      newErrors.company_name = '企業名を入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    if (!validateForm()) return;

    setSubmitting(true);

    try {
      const response = await lpService.registerAndAnalyze({
        email: formData.email,
        service_url: serviceUrl,
        name: formData.name,
        company_name: formData.company_name,
      });

      // Navigate to complete page with email and slug
      const slug = response.report_slug || '';
      navigate(
        `/complete?email=${encodeURIComponent(formData.email)}${slug ? `&slug=${slug}` : ''}`,
        { replace: true }
      );
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'エラーが発生しました。もう一度お試しください。';
      setSubmitError(message);
      setSubmitting(false);
    }
  };

  return (
    <div
      className="landing-page"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div style={{ width: '100%', maxWidth: '512px' }}>
        <AnimatePresence mode="wait">
          {!analysisComplete ? (
            /* --- Analysis Animation --- */
            <motion.div
              key="analyzing"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.4 }}
            >
              <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                <h2
                  style={{
                    fontSize: '24px',
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: 'var(--lp-text-primary)',
                    lineHeight: 1.4,
                  }}
                >
                  AIがあなたのサービスを分析しています...
                </h2>
              </div>

              <div style={{ marginTop: '32px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <AnimatePresence mode="popLayout">
                  {steps.map((step, i) => {
                    if (i >= visibleSteps) return null;
                    return (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}
                      >
                        <div
                          style={{
                            marginTop: '2px',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <svg
                            style={{
                              animation: 'spin 1s linear infinite',
                              height: '16px',
                              width: '16px',
                              color: 'var(--lp-accent-green)',
                            }}
                            viewBox="0 0 24 24"
                          >
                            <circle
                              style={{ opacity: 0.25 }}
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="3"
                              fill="none"
                            />
                            <path
                              style={{ opacity: 0.75 }}
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                        </div>
                        <span style={{ fontSize: '15px', color: 'var(--lp-text-secondary)' }}>
                          {step.label}
                        </span>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            </motion.div>
          ) : (
            /* --- Analysis Complete + Lead Capture Form --- */
            <motion.div
              key="lead-form"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Completion Message */}
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.1 }}
                  style={{
                    width: '64px',
                    height: '64px',
                    margin: '0 auto',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(62, 178, 152, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px',
                  }}
                >
                  <svg
                    style={{ width: '32px', height: '32px', color: 'var(--lp-accent-green)' }}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </motion.div>
                <h2
                  style={{
                    fontSize: '24px',
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: 'var(--lp-text-primary)',
                    lineHeight: 1.4,
                  }}
                >
                  サービスの分析が完了しました
                </h2>
                <p
                  style={{
                    marginTop: '12px',
                    fontSize: '16px',
                    color: 'var(--lp-text-secondary)',
                    lineHeight: 1.7,
                  }}
                >
                  これより、戦略レポートの作成と
                  <br />
                  バナークリエイティブの制作に入ります。
                  <br />
                  完了次第、メールにてお届けします。
                </p>
              </div>

              {/* Lead Capture Form */}
              <motion.form
                onSubmit={handleSubmit}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <div
                  style={{
                    padding: '24px',
                    borderRadius: '16px',
                    border: '1px solid var(--lp-border)',
                    backgroundColor: '#fff',
                  }}
                >
                  <h3
                    style={{
                      fontSize: '18px',
                      fontWeight: 700,
                      color: 'var(--lp-text-primary)',
                      marginBottom: '24px',
                    }}
                  >
                    レポートのお届け先
                  </h3>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {fields.map((field) => (
                      <div key={field.name}>
                        <label
                          style={{
                            display: 'block',
                            fontSize: '14px',
                            fontWeight: 500,
                            color: 'var(--lp-text-primary)',
                            marginBottom: '6px',
                          }}
                        >
                          {field.label}
                          <span style={{ color: 'var(--lp-accent-green)', marginLeft: '4px' }}>*</span>
                        </label>
                        <input
                          type={field.type}
                          placeholder={field.placeholder}
                          value={formData[field.name]}
                          onChange={(e) => handleChange(field.name, e.target.value)}
                          style={{
                            width: '100%',
                            height: '48px',
                            padding: '0 16px',
                            borderRadius: '12px',
                            border: `1px solid ${
                              errors[field.name] ? '#f87171' : 'var(--lp-border-strong)'
                            }`,
                            backgroundColor: '#fff',
                            fontSize: '16px',
                            color: 'var(--lp-text-primary)',
                            outline: 'none',
                            transition: 'border-color 0.2s',
                            boxSizing: 'border-box',
                          }}
                          onFocus={(e) => {
                            if (!errors[field.name]) {
                              e.target.style.borderColor = 'var(--lp-accent-green)';
                            }
                          }}
                          onBlur={(e) => {
                            if (!errors[field.name]) {
                              e.target.style.borderColor = 'var(--lp-border-strong)';
                            }
                          }}
                        />
                        {errors[field.name] && (
                          <p style={{ marginTop: '6px', fontSize: '13px', color: '#ef4444' }}>
                            {errors[field.name]}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>

                  {submitError && (
                    <p
                      style={{
                        marginTop: '16px',
                        fontSize: '14px',
                        color: '#ef4444',
                        textAlign: 'center',
                      }}
                    >
                      {submitError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      marginTop: '24px',
                      width: '100%',
                      height: '56px',
                      borderRadius: '9999px',
                      backgroundColor: 'var(--lp-accent-green)',
                      color: '#fff',
                      fontSize: '17px',
                      fontWeight: 600,
                      border: 'none',
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      opacity: submitting ? 0.6 : 1,
                      transition: 'opacity 0.2s',
                    }}
                  >
                    {submitting ? (
                      <span
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                      >
                        <svg
                          style={{
                            animation: 'spin 1s linear infinite',
                            height: '20px',
                            width: '20px',
                          }}
                          viewBox="0 0 24 24"
                        >
                          <circle
                            style={{ opacity: 0.25 }}
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            style={{ opacity: 0.75 }}
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                        送信中...
                      </span>
                    ) : (
                      'レポートを受け取る'
                    )}
                  </button>
                </div>

              </motion.form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Spinner animation keyframes */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AnalyzingPage;
